/**
 * The signed-in account id, for PostHog's `identify` (the repo's existing
 * identity mechanism, see `apps/app/src/context/analytics-identity.tsx`).
 * Read from Better Auth's session endpoint through the same-origin
 * `/api/auth` proxy. Only the id is read: no email, name or role is kept or
 * sent. Null for a signed-out visitor or when the lookup fails.
 *
 * @param fetcher - The fetch implementation, injectable for tests.
 * @returns The signed-in account id, or null.
 */
export const fetchSessionUserId = async (
  fetcher: typeof fetch = fetch
): Promise<string | null> => {
  try {
    const response = await fetcher("/api/auth/get-session", {
      headers: { accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    const body: unknown = await response.json();

    if (typeof body !== "object" || body === null || !("user" in body)) {
      return null;
    }

    const { user } = body;

    if (typeof user !== "object" || user === null || !("id" in user)) {
      return null;
    }

    return typeof user.id === "string" && user.id.length > 0 ? user.id : null;
  } catch {
    return null;
  }
};
